import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';
import {type SxProps, type Theme, alpha} from '@mui/material/styles';
import {useEffect, useRef} from 'react';
import type * as React from 'react';
import LoadingContainer from '../../Feedback/LoadingContainer';
import PaginationControls from '../../Navigation/PaginationControls';
import {usePagination} from '../../Navigation/PaginationControls/hooks';
import Tooltip from '../Tooltip';

type DataType = 'string' | 'number' | 'actions' | 'checkbox';

export type SortDirection = 'asc' | 'desc';

export type TableSort = {
  key: string;
  direction: SortDirection;
};

export type TableColumn = {
  name: string;
  dataType: DataType;
  component?: React.ReactNode;
  info?: string;
  width?: number | string;
  /** Identifies this column to `sort`. Paired with `onSortChange`, makes the header clickable. */
  sortKey?: string;
  /** Direction applied the first time this column is sorted. Defaults to `'asc'`. */
  defaultSortDirection?: SortDirection;
};

interface PaginationOptions {
  labelled: boolean;
  sizeControl: boolean;
}

interface TableProps {
  /** Drives the header affordance only; reordering the rows remains the caller's job. */
  sort?: TableSort;
  onSortChange?: (sort: TableSort) => void;
  columns: readonly TableColumn[];
  numRows: number;
  isLoading?: boolean;
  customIdPrefix?: string;
  maxHeight?: number;
  title?: string;
  bodyCellStyleOverrides?: object;
  gutter?: number;
  selectedRow?: number;
  pagination?: PaginationOptions;
  itemLabel?: [singular: string, plural: string];
  dataKey?: string;
  itemKeys?: string[];
  emptyState?: React.ReactNode;
  cellRenderer: (row: number, col: number) => React.ReactNode;
  onClickRow?: (row: number) => void;
  hideBorders?: boolean;
  header?: React.ReactNode;
}

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    scrollMarginTop: 16,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 2,
  },
  emptyState: {
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    height: '50%',
    textAlign: 'center',
    flexWrap: 'wrap',
  },
  clickable: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  },
  table: {
    position: 'relative',
    flex: 1,
    borderCollapse: 'separate',
    height: 'max-content',
  },
  headerCell: {
    color: 'text.secondary',
    borderBottom: 0,
    borderRadius: 2,
    height: 30,
    textWrap: 'nowrap',
  },
  head: {
    zIndex: 1,
    backgroundColor: 'background.paper',
  },
  bodyCell: (theme: Theme, isSelected: boolean) => ({
    position: 'relative',
    borderBottom: 0,
    borderTop: 1,
    borderColor: 'divider',
    height: 50,
    p: 2,
    backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.12) : '',
  }),
  tbody: {
    overflow: 'auto',
  },
  noBorder: {
    border: 0,
  },
};

const cellStyles: Record<DataType, object> = {
  string: {},
  number: {
    textAlign: 'right',
  },
  actions: {
    textAlign: 'center',
  },
  checkbox: {
    p: 0,
    pl: 1,
    textAlign: 'center',
    width: 30,
  },
};

const nextSort = (column: TableColumn, sort: TableSort | undefined): TableSort => {
  const key = column.sortKey as string;
  if (sort?.key === key) {
    return {key, direction: sort.direction === 'asc' ? 'desc' : 'asc'};
  }
  return {key, direction: column.defaultSortDirection ?? 'asc'};
};

function Table({
  title,
  isLoading,
  columns,
  numRows,
  cellRenderer,
  onClickRow,
  customIdPrefix,
  maxHeight,
  bodyCellStyleOverrides = {},
  gutter = 3,
  selectedRow,
  pagination,
  itemLabel = ['row', 'rows'],
  itemKeys,
  dataKey,
  emptyState = null,
  hideBorders = false,
  header,
  sort,
  onSortChange,
}: TableProps) {
  const prefix = customIdPrefix ? `${customIdPrefix}-` : '';
  const tableRef = useRef<HTMLDivElement>(null);
  const paginationState = usePagination(new Array(numRows), itemLabel);
  const {itemsToShow, setCurrentPage, rowsPerPage, currentPage} = paginationState;
  const rowsToShow = pagination ? itemsToShow.length : numRows;

  useEffect(() => {
    if (dataKey) {
      setCurrentPage(0);
    }
  }, [dataKey, setCurrentPage]);

  if (!numRows && !isLoading) {
    return emptyState ? (
      <Box>{emptyState}</Box>
    ) : (
      <Box sx={styles.emptyState}>
        <Box>
          <Typography color="text.primary" variant="h5" pb={2}>
            No results found
          </Typography>
          <Typography color="text.primary">Please check your filters and try again.</Typography>
        </Box>
      </Box>
    );
  }

  const getColumnStyle = (column: TableColumn): SxProps<Theme> => {
    const baseStyle: SxProps<Theme> = {
      ...styles.headerCell,
      ...cellStyles[column.dataType],
    };

    if (column.width) {
      return {
        ...baseStyle,
        width: column.width,
      };
    }

    return baseStyle;
  };

  return (
    <Box sx={[styles.root, {gap: gutter}]} ref={tableRef}>
      {(header || pagination?.labelled) && (
        <Box sx={styles.header}>
          {pagination?.labelled ? (
            <Typography variant="body1">{`Showing ${paginationState.label}`}</Typography>
          ) : null}
          {header}
        </Box>
      )}
      <Paper sx={hideBorders ? styles.noBorder : {}}>
        {title && (
          <Typography component="h2" variant="h6" pl={2} pt={3} pb={2}>
            {title}
          </Typography>
        )}
        <LoadingContainer loading={isLoading}>
          <TableContainer
            sx={{
              maxHeight,
              minHeight: isLoading ? '125px' : 'auto',
            }}
          >
            <MuiTable sx={styles.table} aria-label="simple table">
              <TableHead sx={styles.head}>
                <TableRow>
                  {columns.map((column, index) => {
                    const sortable =
                      Boolean(column.sortKey) && Boolean(onSortChange) && !column.component;
                    const activeDirection: SortDirection | undefined =
                      sortable && sort && column.sortKey === sort.key ? sort.direction : undefined;
                    const heading = sortable ? (
                      <TableSortLabel
                        active={activeDirection !== undefined}
                        direction={activeDirection ?? column.defaultSortDirection ?? 'asc'}
                        onClick={() => onSortChange?.(nextSort(column, sort))}
                      >
                        <Typography component="span" fontWeight={500}>
                          {column.name}
                        </Typography>
                      </TableSortLabel>
                    ) : (
                      <Typography fontWeight={500}>{column.name}</Typography>
                    );
                    return (
                      <TableCell
                        key={`${column.name}${index}`}
                        sx={getColumnStyle(column)}
                        sortDirection={activeDirection ?? false}
                      >
                        {column.component ? (
                          column.component
                        ) : (
                          <Box display="inline-flex" alignItems="center">
                            {heading}
                            {column.info && (
                              <Tooltip title={column.info}>
                                <InfoOutlined
                                  fontSize="small"
                                  sx={{ml: 1, mt: 1}}
                                  color="info"
                                  aria-label={column.info}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody sx={styles.tbody}>
                {[...Array(rowsToShow)].map((_, rowIndex) => {
                  const dataIndex = rowIndex + rowsPerPage * currentPage;
                  return (
                    <TableRow
                      sx={onClickRow ? styles.clickable : {}}
                      onClick={() => onClickRow?.(dataIndex)}
                      key={itemKeys ? itemKeys[dataIndex] : rowIndex}
                      id={`${prefix}${rowIndex}`}
                    >
                      {columns.map((column, colIndex) => (
                        <TableCell
                          key={
                            itemKeys
                              ? `${itemKeys[dataIndex]}-${colIndex}`
                              : `${colIndex}${rowIndex}`
                          }
                          sx={[
                            (theme) => styles.bodyCell(theme, dataIndex === selectedRow),
                            hideBorders ? styles.noBorder : {},
                            cellStyles[column.dataType],
                            bodyCellStyleOverrides,
                            column.width ? {width: column.width} : {},
                          ]}
                          id={`${prefix}${rowIndex}-${colIndex}`}
                        >
                          {cellRenderer(dataIndex, colIndex)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </MuiTable>
          </TableContainer>
        </LoadingContainer>
      </Paper>
      {pagination && (
        <PaginationControls
          paginationState={paginationState}
          itemLabel={itemLabel}
          includeSizeControls={pagination.sizeControl}
          containerRef={tableRef}
        />
      )}
    </Box>
  );
}

export default Table;
